/**
 * FlushScheduler - Background flush scheduler for save queue
 *
 * Implements background flush operations with:
 * - requestIdleCallback scheduling for non-blocking execution
 * - Exponential backoff retry strategy
 * - Concurrent request limiting
 * - Network status monitoring
 * - Version conflict handling
 *
 * @module features/storage/flushScheduler
 * @see SPEC-006-background-cloud-save-queue
 * @see Requirements 2.7, 2.8, 3.1-3.8, 4.1-4.5, 5.3, 6.2-6.7
 */

import type { SaveQueue, PendingSave } from './saveQueue';
import type { DocApiClient } from './docApiClient';
import type { FlushResult, FlushError } from './types';

/**
 * Options for FlushScheduler configuration
 */
export interface FlushSchedulerOptions {
  /** Maximum concurrent flush requests per document (default: 2) */
  maxConcurrent?: number;
  /** Request timeout in milliseconds (default: 30000) */
  timeoutMs?: number;
  /** Base retry delay in milliseconds (default: 500) */
  baseRetryDelayMs?: number;
  /** Maximum retry delay in milliseconds (default: 60000) */
  maxRetryDelayMs?: number;
  /** Maximum retry attempts before marking as failed (default: 8) */
  maxAttempts?: number;
  /** Callback when a conflict is detected */
  onConflict?: (docId: string, serverVersionId: string) => void;
  /** Callback when flush status changes */
  onFlushComplete?: (result: FlushResult) => void;
  /** Callback when an error occurs after all retries */
  onError?: (error: FlushError, save: PendingSave) => void;
  /** Callback when a save succeeds, provides docId and new versionId */
  onSuccess?: (docId: string, newVersionId: string) => void;
}

/**
 * Default configuration values
 */
const DEFAULT_MAX_CONCURRENT = 2;
const DEFAULT_TIMEOUT_MS = 30000;
const DEFAULT_BASE_RETRY_DELAY_MS = 500;
const DEFAULT_MAX_RETRY_DELAY_MS = 60000;
const DEFAULT_MAX_ATTEMPTS = 8;

/**
 * Calculate retry delay using exponential backoff
 *
 * Formula: min(2^attempt * baseDelay, maxDelay)
 *
 * @param attempt - Current attempt number (0-based)
 * @param baseDelayMs - Base delay in milliseconds (default: 500)
 * @param maxDelayMs - Maximum delay in milliseconds (default: 60000)
 * @returns Delay in milliseconds
 *
 * @see Requirements 3.2 - Retry delay SHALL be calculated as min(2^attempt * 500ms, 60000ms)
 */
export function calculateRetryDelay(
  attempt: number,
  baseDelayMs: number = DEFAULT_BASE_RETRY_DELAY_MS,
  maxDelayMs: number = DEFAULT_MAX_RETRY_DELAY_MS
): number {
  const delay = Math.pow(2, attempt) * baseDelayMs;
  return Math.min(delay, maxDelayMs);
}

/**
 * FlushScheduler - Background flush scheduler for save queue
 *
 * Features:
 * - Non-blocking flush using requestIdleCallback
 * - Exponential backoff retry strategy
 * - Concurrent request limiting (max 2 per document)
 * - Network status monitoring with auto-flush on reconnect
 * - Version conflict detection and handling
 *
 * @see Requirements 2.7, 2.8, 3.1-3.8, 4.1-4.5, 5.3, 6.2-6.7
 */
export class FlushScheduler {
  private readonly maxConcurrent: number;
  private readonly timeoutMs: number;
  private readonly baseRetryDelayMs: number;
  private readonly maxRetryDelayMs: number;
  private readonly maxAttempts: number;

  private activeFlushes: Map<string, AbortController> = new Map();
  private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true;
  private isRunning: boolean = false;
  private idleCallbackId: number | null = null;
  private retryTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();

  // Event handlers bound to this instance
  private boundOnOnline: () => void;
  private boundOnOffline: () => void;

  constructor(
    private readonly queue: SaveQueue,
    private readonly apiClient: DocApiClient,
    private readonly options: FlushSchedulerOptions = {}
  ) {
    this.maxConcurrent = options.maxConcurrent ?? DEFAULT_MAX_CONCURRENT;
    this.timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.baseRetryDelayMs = options.baseRetryDelayMs ?? DEFAULT_BASE_RETRY_DELAY_MS;
    this.maxRetryDelayMs = options.maxRetryDelayMs ?? DEFAULT_MAX_RETRY_DELAY_MS;
    this.maxAttempts = options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;

    // Bind event handlers
    this.boundOnOnline = this.onOnline.bind(this);
    this.boundOnOffline = this.onOffline.bind(this);
  }

  /**
   * Start the flush scheduler
   *
   * Begins listening for network events and schedules initial flush.
   *
   * @see Requirements 3.7, 3.8, 5.3
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    // Add network event listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.boundOnOnline);
      window.addEventListener('offline', this.boundOnOffline);
    }

    // Schedule initial flush if online
    if (this.isOnline) {
      this.scheduleFlush();
    }
  }

  /**
   * Stop the flush scheduler
   *
   * Cancels all pending flushes and removes event listeners.
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;

    // Remove network event listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.boundOnOnline);
      window.removeEventListener('offline', this.boundOnOffline);
    }

    // Cancel idle callback
    if (this.idleCallbackId !== null) {
      if (typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(this.idleCallbackId);
      }
      this.idleCallbackId = null;
    }

    // Cancel all retry timeouts
    for (const timeoutId of this.retryTimeouts.values()) {
      clearTimeout(timeoutId);
    }
    this.retryTimeouts.clear();

    // Abort all active flushes
    for (const controller of this.activeFlushes.values()) {
      controller.abort();
    }
    this.activeFlushes.clear();
  }

  /**
   * Get the number of currently active flush requests
   */
  getActiveCount(): number {
    return this.activeFlushes.size;
  }

  /**
   * Check if the scheduler is currently running
   */
  isSchedulerRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Check if the browser is online
   */
  getIsOnline(): boolean {
    return this.isOnline;
  }

  /**
   * Manually trigger an immediate flush
   *
   * @param docId - Optional document ID to flush only saves for that document
   * @returns Array of flush results
   *
   * @see Requirements 3.6 - Support manual retry via "Retry Now" button
   */
  async flushNow(docId?: string): Promise<FlushResult[]> {
    if (!this.isOnline) {
      return [];
    }

    const saves = await this.queue.getAll(docId);
    const pendingSaves = saves.filter((s) => s.status === 'pending' || s.status === 'failed');

    const results: FlushResult[] = [];
    for (const save of pendingSaves) {
      // Respect concurrent limit
      if (this.activeFlushes.size >= this.maxConcurrent) {
        break;
      }

      const result = await this.executeFlush(save);
      results.push(result);
    }

    return results;
  }

  /**
   * Schedule a flush using requestIdleCallback
   *
   * Uses requestIdleCallback to avoid blocking the main thread.
   * Falls back to setTimeout if requestIdleCallback is not available.
   *
   * @see Requirements 2.7, 2.8, 6.2
   */
  scheduleFlush(): void {
    if (!this.isRunning || !this.isOnline) return;

    // Don't schedule if already scheduled
    if (this.idleCallbackId !== null) return;

    const doFlush = async () => {
      this.idleCallbackId = null;

      if (!this.isRunning || !this.isOnline) return;

      // Get pending saves
      const saves = await this.queue.getAll();
      const pendingSaves = saves.filter(
        (s) => s.status === 'pending' && !this.activeFlushes.has(s.saveId)
      );

      // Process saves up to concurrent limit
      const toProcess = pendingSaves.slice(0, this.maxConcurrent - this.activeFlushes.size);

      for (const save of toProcess) {
        // Don't await - let them run concurrently
        this.executeFlush(save).catch((err) => {
          console.error('[FlushScheduler] Unexpected error during flush:', err);
        });
      }

      // Schedule next flush if there are more pending saves
      const remainingCount = await this.queue.getQueueLength();
      if (remainingCount > 0 && this.isRunning && this.isOnline) {
        // Use shorter interval if queue is large
        const interval = remainingCount > 20 ? 100 : 1000;
        setTimeout(() => this.scheduleFlush(), interval);
      }
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if (typeof requestIdleCallback !== 'undefined') {
      this.idleCallbackId = requestIdleCallback(doFlush, { timeout: 5000 });
    } else {
      // Fallback for environments without requestIdleCallback
      setTimeout(doFlush, 0);
      this.idleCallbackId = -1; // Mark as scheduled
    }
  }

  /**
   * Execute a single flush operation
   *
   * Handles the complete flush lifecycle:
   * 1. Mark save as flushing
   * 2. Send API request
   * 3. Handle success/failure
   * 4. Schedule retry if needed
   *
   * @param save - The pending save to flush
   * @returns Flush result
   *
   * @see Requirements 2.4, 2.5, 3.1, 3.3, 3.4, 6.7
   */
  private async executeFlush(save: PendingSave): Promise<FlushResult> {
    const { saveId, docId, markdown, baseVersionId, meta } = save;

    // Check concurrent limit
    if (this.activeFlushes.size >= this.maxConcurrent) {
      return {
        success: false,
        saveId,
        error: {
          type: 'network',
          message: 'Concurrent flush limit reached',
        },
      };
    }

    // Create abort controller for this flush
    const controller = new AbortController();
    this.activeFlushes.set(saveId, controller);

    // Mark as flushing
    await this.queue.updateStatus(saveId, 'flushing');

    try {
      // Execute the API call
      const response = await this.apiClient.putDoc(docId, {
        markdown,
        baseVersionId,
        meta,
      });

      // Success - remove from queue
      await this.queue.dequeue(saveId);

      const result: FlushResult = {
        success: true,
        saveId,
        newVersionId: response.versionId,
      };

      // Notify success callback with docId and new versionId
      this.options.onSuccess?.(docId, response.versionId);

      // Notify flush complete callback
      this.options.onFlushComplete?.(result);

      // Schedule next flush
      this.scheduleFlush();

      return result;
    } catch (error) {
      const flushError = error as FlushError;

      // Handle conflict specially
      if (flushError.type === 'conflict') {
        return this.handleConflict(save, flushError);
      }

      // Handle other errors with retry
      return this.handleFlushError(save, flushError);
    } finally {
      this.activeFlushes.delete(saveId);
    }
  }

  /**
   * Determine if an error type is retryable
   *
   * Non-retryable errors:
   * - unauthorized: Authentication issues won't be fixed by retrying
   * - conflict: Requires user intervention (handled separately)
   *
   * Retryable errors:
   * - network: Temporary network issues
   * - timeout: Request timeout
   * - server: Temporary server errors (5xx)
   *
   * @param errorType - The type of error
   * @returns Whether the error is retryable
   *
   * @see Requirements 3.1 - Only retry transient errors
   */
  private isRetryableError(errorType: FlushError['type']): boolean {
    // unauthorized and conflict errors should NOT be retried
    return errorType !== 'unauthorized' && errorType !== 'conflict';
  }

  /**
   * Handle flush error with retry logic
   *
   * Implements exponential backoff retry strategy.
   * Marks as failed after max attempts or for non-retryable errors.
   *
   * @param save - The save that failed
   * @param error - The error that occurred
   * @returns Flush result
   *
   * @see Requirements 3.1, 3.2, 3.3, 3.4
   */
  private async handleFlushError(save: PendingSave, error: FlushError): Promise<FlushResult> {
    const { saveId } = save;

    // Check if error is retryable - unauthorized errors should NOT be retried
    if (!this.isRetryableError(error.type)) {
      // Mark as failed immediately - no retry for non-retryable errors
      await this.queue.updateStatus(saveId, 'failed', error.message);

      const result: FlushResult = {
        success: false,
        saveId,
        error,
      };

      // Notify error callback
      this.options.onError?.(error, save);
      this.options.onFlushComplete?.(result);

      return result;
    }

    // Increment attempt count
    const newAttempt = await this.queue.incrementAttempt(saveId);

    // Check if max attempts reached
    if (newAttempt >= this.maxAttempts) {
      // Mark as failed
      await this.queue.updateStatus(saveId, 'failed', error.message);

      const result: FlushResult = {
        success: false,
        saveId,
        error,
      };

      // Notify error callback
      this.options.onError?.(error, save);
      this.options.onFlushComplete?.(result);

      return result;
    }

    // Schedule retry with exponential backoff
    const delay = calculateRetryDelay(newAttempt, this.baseRetryDelayMs, this.maxRetryDelayMs);

    // Mark as pending for retry
    await this.queue.updateStatus(saveId, 'pending', error.message);

    // Schedule retry
    const timeoutId = setTimeout(() => {
      this.retryTimeouts.delete(saveId);
      if (this.isRunning && this.isOnline) {
        this.scheduleFlush();
      }
    }, delay);

    this.retryTimeouts.set(saveId, timeoutId);

    return {
      success: false,
      saveId,
      error,
    };
  }

  /**
   * Handle version conflict (HTTP 412)
   *
   * Fetches the latest version and notifies via callback.
   * Does NOT automatically overwrite server content.
   *
   * @param save - The save that conflicted
   * @param error - The conflict error
   * @returns Flush result
   *
   * @see Requirements 4.1, 4.2, 4.4, 4.5
   */
  private async handleConflict(save: PendingSave, error: FlushError): Promise<FlushResult> {
    const { saveId, docId } = save;

    try {
      // Fetch latest version from server
      const latestDoc = await this.apiClient.getDoc(docId);

      // Mark save as failed (conflict)
      await this.queue.updateStatus(saveId, 'failed', 'Version conflict detected');

      // Notify conflict callback
      this.options.onConflict?.(docId, latestDoc.versionId);

      const result: FlushResult = {
        success: false,
        saveId,
        error: {
          type: 'conflict',
          message: 'Version conflict detected. Server has a newer version.',
          statusCode: 412,
        },
      };

      this.options.onFlushComplete?.(result);

      return result;
    } catch (fetchError) {
      // Failed to fetch latest version - treat as regular error
      return this.handleFlushError(save, error);
    }
  }

  /**
   * Handle online event
   *
   * Triggers immediate flush when network is restored.
   *
   * @see Requirements 3.7, 3.8
   */
  private onOnline(): void {
    this.isOnline = true;

    if (this.isRunning) {
      // Immediately schedule flush
      this.scheduleFlush();
    }
  }

  /**
   * Handle offline event
   *
   * Pauses flush operations when network is unavailable.
   *
   * @see Requirements 5.3
   */
  private onOffline(): void {
    this.isOnline = false;

    // Cancel scheduled idle callback
    if (this.idleCallbackId !== null) {
      if (typeof cancelIdleCallback !== 'undefined') {
        cancelIdleCallback(this.idleCallbackId);
      }
      this.idleCallbackId = null;
    }

    // Note: We don't abort active flushes - let them complete or fail naturally
  }
}
